import { Link } from '@g2u/ui-kit';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { getCustomerProducts, putCustomerProduct } from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem, PrepaidProductStatus, PrepaidProductStatusLabels } from 'types';

export const useCustomerProducts = (customerId?: string) => {
  const navigate = useNavigate();

  const {
    data,
    isLoading: isProductsLoading,
    refetch,
  } = useQuery(['get-products', Number(customerId)!, ''], getCustomerProducts, {
    enabled: !!customerId,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate, isLoading: isProductsTransfering } = useMutation(putCustomerProduct, {
    onSuccess: (_, variables) => {
      const customerUrl = `/customer/edit/${variables.customerId}`;
      const toastMsg = variables.status ? (
        <>Product has been marked as {PrepaidProductStatusLabels[variables.status].toLowerCase()}</>
      ) : (
        <>
          Product has been transferred to another customer account{' '}
          {variables.name && (
            <>
              - <Link.$ onClick={() => navigate(`${customerUrl}/details`)}>{variables.name}</Link.$>
            </>
          )}
        </>
      );

      Toast.show({
        description: toastMsg,
        type: Toast.Type.Success,
      });

      // If mutate has been passed with param `status` consider the putCustomerProduct has been intended to use for updating customer product status
      // else consider it is for transfer
      variables.status ? refetch() : navigate(`${customerUrl}/discounts`);
    },
    onError: (error) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const transferProducts = (customerProductId: number, customerId: number, name: string) => {
    mutate({ customerProductId, customerId, name });
  };

  const updateCustomerProductStatus = (customerProductId: number, status: PrepaidProductStatus) => {
    mutate({ customerProductId, status });
  };

  return {
    products: data,
    transferProducts,
    updateCustomerProductStatus,
    isProductsLoading: isProductsLoading || isProductsTransfering,
  };
};
